import React, { useState } from 'react';
import { MetricItem } from '../../types/Lead';
import { getMetricDisplayName } from '../../config/metrics';

interface ExpandableMetricCardProps {
  metricKey: string;
  metric: MetricItem;
}

const ExpandableMetricCard: React.FC<ExpandableMetricCardProps> = ({ metricKey, metric }) => {
  // Use localStorage to persist expanded state across tab switches
  const storageKey = `metric-${metricKey}-expanded`;
  const [isExpanded, setIsExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored === 'true';
    } catch {
      return false;
    }
  });

  const getScoreColor = (score: number): string => {
    if (score >= 3.5) return '#22c55e';
    if (score >= 2.5) return '#f59e0b';
    if (score >= 1.5) return '#ef4444';
    return '#6b7280';
  };

  const handleCardClick = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);

    // Save expanded state to localStorage
    try {
      localStorage.setItem(storageKey, newExpandedState.toString());
    } catch (error) {
      console.warn('Failed to save expanded state to localStorage:', error);
    }
  };

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        backgroundColor: '#f9fafb',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        transform: isExpanded ? 'scale(1.02)' : 'scale(1)',
        boxShadow: isExpanded ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}
      onClick={handleCardClick}
      onMouseEnter={(e) => {
        if (!isExpanded) {
          e.currentTarget.style.backgroundColor = '#f3f4f6';
          e.currentTarget.style.borderColor = '#d1d5db';
        }
      }}
      onMouseLeave={(e) => {
        if (!isExpanded) {
          e.currentTarget.style.backgroundColor = '#f9fafb';
          e.currentTarget.style.borderColor = '#e5e7eb';
        }
      }}
    >
      {/* Header with metric name and score */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px'
      }}>
        <h5 style={{ fontWeight: '500', color: '#374151', margin: 0 }}>
          {getMetricDisplayName(metricKey)}
        </h5>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px', fontWeight: 'bold', color: getScoreColor(metric.score) }}>
            {metric.score}
          </span>
          <svg
            width="16"
            height="16"
            fill="none"
            stroke="#6b7280"
            viewBox="0 0 24 24"
            style={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Description */}
      <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
        {metric.description}
      </p>

      {/* Reason */}
      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
        {metric.reason}
      </p>

      {/* Expanded content */}
      {isExpanded && (
        <div style={{
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px solid #e5e7eb',
          animation: 'fadeIn 0.3s ease'
        }}>
          {/* Sources section */}
          {metric.sources && metric.sources.length > 0 && metric.sources.some(source => source && source.trim()) && (
            <div style={{ marginBottom: '12px' }}>
              <h6 style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#374151',
                margin: '0 0 8px 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Sources
              </h6>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {metric.sources
                  .filter(source => source && source.trim()) // Only show non-empty sources
                  .map((source, index) => (
                  <a
                    key={index}
                    href={source}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 8px',
                      backgroundColor: '#eff6ff',
                      color: '#1d4ed8',
                      textDecoration: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      border: '1px solid #dbeafe',
                      transition: 'all 0.2s ease',
                      wordBreak: 'break-all'
                    }}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent click from bubbling up to the card
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#dbeafe';
                      e.currentTarget.style.borderColor = '#93c5fd';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#eff6ff';
                      e.currentTarget.style.borderColor = '#dbeafe';
                    }}
                  >
                    <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    {source}
                  </a>
                ))}
              </div>
            </div>
          )}


        </div>
      )}
    </div>
  );
};

export default ExpandableMetricCard;